import { useEffect, useMemo, useRef, useState } from 'react';
import { membershipState, recordEntry, type CatalogItem, type Customer, type LicenceStatus } from '@makila/pos-core';
import { CheckCircle2, DoorOpen, LayoutDashboard, Moon, Plug, RefreshCw, Search, ShoppingCart, Sun, Undo2, Wallet, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { HardwareDialog } from '../components/HardwareDialog';
import { ManagerApproval } from '../components/ManagerApproval';
import { Rail, RailItem, RailTool } from '../components/Rail';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';

interface Member { id: string; name: string; nuit: string | null; phone: string | null; active: boolean; memberUntil?: string | null }
const dmy = (d: string) => d.split('-').reverse().join('/');

interface Props {
  store: IdbTerminalStore; device: DeviceState; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  /** Renovar: a venda abre com o sócio escolhido. */
  onRenew: (c: Customer) => void;
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
}

/**
 * Entradas (ginásio, academia; docs/14 Fase 1): lê-se o cartão (QR com o
 * código do sócio) ou procura-se pelo nome ou telefone; verde = em dia,
 * vermelho = mensalidade em atraso. Cada entrada fica registada; deixar
 * entrar em atraso precisa de um gerente.
 */
export function AccessScreen(p: Props) {
  const [members, setMembers] = useState<Member[]>([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Member | null>(null);
  const [log, setLog] = useState<{ name: string; ok: boolean; at: string }[]>([]);
  const [approve, setApprove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void p.store.catalog('customer').then((all) => setMembers(all.map((i: CatalogItem) => i.data as unknown as Member).filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name, 'pt'))));
  }, [p.store, p.sync?.lastSyncAt]);
  const s = q.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  const found = useMemo(() => (!s ? [] : members.filter((m) => m.id === q.trim() || m.name.toLowerCase().includes(s) || (digits.length >= 4 && (m.phone ?? '').replace(/\D/g, '').includes(digits)) || m.nuit === digits).slice(0, 8)), [members, s, q, digits]);
  const state = sel ? membershipState(sel.memberUntil) : null;

  const enter = async (ok: boolean) => {
    if (!sel) return;
    try {
      await recordEntry(p.store, identity(p.device), { customerId: sel.id, ok, operatorId: p.operator.id });
      setLog((l) => [{ name: sel.name, ok, at: new Date().toTimeString().slice(0, 5) }, ...l].slice(0, 30));
      setSel(null); setQ(''); setApprove(false); p.onSyncNow();
      input.current?.focus();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        <RailItem icon={DoorOpen} label="Entradas" active badge={log.length || null} />
        <RailItem icon={ShoppingCart} label="Vender" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">Entradas</div>
            <div className="truncate text-xs text-muted">{log.length} hoje nesta caixa · {p.operator.name}</div>
          </div>
          <div className="ml-auto"><StatusPill sync={p.sync} /></div>
        </header>
        {error && <div role="alert" className="bg-amber-100 px-4 py-2 text-amber-900">{error}</div>}
        <main className="grid min-h-0 flex-1 gap-6 overflow-y-auto p-4 lg:grid-cols-[1fr_320px] lg:p-6">
          <section className="mx-auto w-full max-w-2xl">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={22} />
              <input ref={input} autoFocus aria-label="Sócio: nome, telefone ou cartão" placeholder="Ler o cartão, ou escrever o nome ou o telefone" value={q}
                onChange={(e) => { setQ(e.target.value); setSel(null); setApprove(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && found.length === 1) { e.preventDefault(); setSel(found[0]!); } }}
                className="h-16 w-full rounded-2xl border border-base bg-[var(--surface)] pl-14 pr-4 text-xl shadow-sm outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10" />
            </div>
            {!sel && found.length > 0 && (
              <ul className="surface mt-2 divide-y divide-[var(--border)] overflow-hidden rounded-2xl">
                {found.map((m) => {
                  const st = membershipState(m.memberUntil);
                  return (
                    <li key={m.id}>
                      <button onClick={() => setSel(m)} className="flex h-14 w-full items-center gap-3 px-4 text-left hover:bg-surface-2">
                        <span className={clsx('h-3 w-3 rounded-full', st.ok ? 'bg-green-600' : 'bg-brand-700')} />
                        <span className="flex-1 font-medium">{m.name}</span>
                        <span className="text-sm text-muted">{m.memberUntil ? `até ${dmy(m.memberUntil)}` : 'sem mensalidade'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {s && !found.length && !sel && <p className="mt-6 text-center text-muted">Nenhum sócio encontrado.</p>}

            {sel && state && (
              <div role="status" aria-label={state.ok ? 'Em dia' : 'Em atraso'} className={clsx('mt-6 rounded-3xl p-6 text-white shadow-lg', state.ok ? 'bg-green-700' : 'bg-brand-700')}>
                <div className="flex items-center gap-4">
                  {state.ok ? <CheckCircle2 size={56} /> : <XCircle size={56} />}
                  <div className="min-w-0">
                    <div className="truncate font-display text-3xl font-bold">{sel.name}</div>
                    <div className="mt-1 text-lg">
                      {state.ok
                        ? `Em dia até ${dmy(state.until!)}${state.daysLeft! <= 5 ? ` · faltam ${state.daysLeft} dias` : ''}`
                        : state.until ? `Mensalidade em atraso desde ${dmy(state.until)}` : 'Sem mensalidade paga'}
                    </div>
                  </div>
                </div>
                <div className="mt-6 grid gap-2 sm:grid-cols-2">
                  {state.ok
                    ? <button onClick={() => void enter(true)} className="h-16 rounded-2xl bg-white text-xl font-bold text-green-800">Registar entrada</button>
                    : <button onClick={() => setApprove(true)} className="h-16 rounded-2xl border-2 border-white/70 text-lg font-semibold">Deixar entrar (gerente)</button>}
                  <button onClick={() => p.onRenew({ id: sel.id, name: sel.name, nuit: sel.nuit })} className={clsx('h-16 rounded-2xl text-lg font-bold', state.ok ? 'border-2 border-white/70' : 'bg-white text-brand-800')}>Renovar mensalidade</button>
                </div>
                {approve && (
                  <div className="mt-4 rounded-2xl bg-white p-4 text-[var(--text)] dark:bg-[var(--surface)]">
                    <ManagerApproval store={p.store} operator={p.operator} why="Entrada com a mensalidade em atraso" onApproved={() => void enter(false)} />
                  </div>
                )}
              </div>
            )}
          </section>
          <aside className="surface h-fit rounded-2xl p-4">
            <h2 className="mb-2 font-display font-semibold">Últimas entradas</h2>
            {!log.length ? <p className="text-sm text-muted">Ainda ninguém entrou nesta caixa.</p> : (
              <ul className="space-y-1.5 text-sm">
                {log.map((l, i) => <li key={i} className="flex items-center gap-2"><span className={clsx('h-2.5 w-2.5 rounded-full', l.ok ? 'bg-green-600' : 'bg-brand-700')} /><span className="num text-muted">{l.at}</span><span className="flex-1 truncate">{l.name}</span>{!l.ok && <span className="text-xs text-brand-700">em atraso</span>}</li>)}
              </ul>
            )}
          </aside>
        </main>
      </div>
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
    </div>
  );
}
